import {useState,useEffect,useRef,useCallback} from 'react';
import {createPortal} from 'react-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {config} from '../data/config';

const {FiCalendar,FiChevronLeft,FiChevronRight}=FiIcons;

export function formatAustralianDate(val: string | Date | null | undefined): string {
  if (!val) return '—';
  try {
    if (typeof val==='string' && /^\d{4}-\d{2}-\d{2}$/.test(val.trim())) {
      const [y,m,d]=val.trim().split('-');
      return `${d}/${m}/${y}`;
    }
    const d=new Date(val);
    if (isNaN(d.getTime())) return '—';
    return new Intl.DateTimeFormat(config.locale,{timeZone: config.timeZone,day: '2-digit',month: '2-digit',year: 'numeric'}).format(d);
  } catch { return '—'; }
}

export function formatAustralianDateTime(val: string | Date | null | undefined): string {
  if (!val) return '—';
  try {
    const d=new Date(val);
    if (isNaN(d.getTime())) return '—';
    const datePart=new Intl.DateTimeFormat(config.locale,{timeZone: config.timeZone,day: '2-digit',month: '2-digit',year: 'numeric'}).format(d);
    let timePart=new Intl.DateTimeFormat(config.locale,{timeZone: config.timeZone,hour: 'numeric',minute: '2-digit',hour12: true}).format(d);
    timePart=timePart.replace('AM','am').replace('PM','pm');
    return `${datePart}, ${timePart}`;
  } catch { return '—'; }
}

export function formatAustralianTime(val: string | Date | null | undefined): string {
  if (!val) return '—';
  try {
    const d=new Date(val);
    if (isNaN(d.getTime())) return '—';
    let timePart=new Intl.DateTimeFormat(config.locale,{timeZone: config.timeZone,hour: 'numeric',minute: '2-digit',hour12: true}).format(d);
    timePart=timePart.replace('AM','am').replace('PM','pm');
    return timePart;
  } catch { return '—'; }
}

export function getSydneyDateStamp(): string {
  const parts=new Intl.DateTimeFormat(config.locale,{timeZone: config.timeZone,year: 'numeric',month: '2-digit',day: '2-digit'}).formatToParts(new Date());
  const y=parts.find(p=> p.type==='year')?.value;
  const m=parts.find(p=> p.type==='month')?.value;
  const d=parts.find(p=> p.type==='day')?.value;
  return `${y}-${m}-${d}`;
}

export function normaliseDateOnlyValue(input: string): string | null {
  const val=input.trim();
  if (!val) return null;
  const dmyMatch=val.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (dmyMatch) {
    const d=parseInt(dmyMatch[1],10);
    const m=parseInt(dmyMatch[2],10);
    const y=parseInt(dmyMatch[3],10);
    if (m < 1 || m > 12 || d < 1 || d > 31) return null;
    return `${y}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
  }
  const ymdMatch=val.match(/^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})$/);
  if (ymdMatch) {
    const y=parseInt(ymdMatch[1],10);
    const m=parseInt(ymdMatch[2],10);
    const d=parseInt(ymdMatch[3],10);
    if (m < 1 || m > 12 || d < 1 || d > 31) return null;
    return `${y}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
  }
  return null;
}

interface SmartDateFieldProps {
  label?: string;
  value: string;
  onChange: (val: string)=> void;
  required?: boolean;
  disabled?: boolean;
  minDate?: string;
  maxDate?: string;
  id?: string;
  error?: string;
  ariaDescribedBy?: string;
}

export function SmartDateField({label,value,onChange,required,disabled,minDate,maxDate,id,error,ariaDescribedBy}: SmartDateFieldProps) {
  const [displayValue,setDisplayValue]=useState('');
  const [isOpen,setIsOpen]=useState(false);
  const [localError,setLocalError]=useState('');
  const [currentMonth,setCurrentMonth]=useState(new Date().getMonth());
  const [currentYear,setCurrentYear]=useState(new Date().getFullYear());
  const [popoverStyle,setPopoverStyle]=useState<React.CSSProperties>({});
  
  const containerRef=useRef<HTMLDivElement>(null);
  const inputRef=useRef<HTMLInputElement>(null);
  const popoverRef=useRef<HTMLDivElement>(null);

  useEffect(()=> {
    if (value) {
      setDisplayValue(formatAustralianDate(value));
      setLocalError('');
      const parts=value.split('-');
      if (parts.length===3) {
        setCurrentYear(parseInt(parts[0],10));
        setCurrentMonth(parseInt(parts[1],10) - 1);
      }
    } else {
      setDisplayValue('');
    }
  },[value]);

  const handleBlur=()=> {
    if (!displayValue.trim()) {
      setLocalError('');
      if (value !=='') onChange('');
      return;
    }
    const norm=normaliseDateOnlyValue(displayValue);
    if (norm) {
      if (minDate && norm < minDate) {
        setLocalError(`Date cannot be before ${formatAustralianDate(minDate)}`);
        return;
      }
      if (maxDate && norm > maxDate) {
        setLocalError(`Date cannot be after ${formatAustralianDate(maxDate)}`);
        return;
      }
      setLocalError('');
      setDisplayValue(formatAustralianDate(norm));
      if (norm !==value) onChange(norm);
    } else {
      setLocalError('Invalid format. Use DD/MM/YYYY');
    }
  };

  const handleKeyDown=(e: React.KeyboardEvent)=> {
    if (e.key==='Enter') {
      e.preventDefault();
      handleBlur();
      setIsOpen(false);
    } else if (e.key==='Escape') {
      setIsOpen(false);
    }
  };

  const openCalendar=()=> {
    if (disabled) return;
    if (value) {
      const parts=value.split('-');
      if (parts.length===3) {
        setCurrentYear(parseInt(parts[0],10));
        setCurrentMonth(parseInt(parts[1],10) - 1);
      }
    } else {
      const todayParts=getSydneyDateStamp().split('-');
      setCurrentYear(parseInt(todayParts[0],10));
      setCurrentMonth(parseInt(todayParts[1],10) - 1);
    }
    setIsOpen(true);
  };

  const closeCalendar=useCallback(()=> setIsOpen(false),[]);

  useEffect(()=> {
    if (!isOpen || !containerRef.current) return;
    const updatePosition=()=> {
      if (!containerRef.current) return;
      const rect=containerRef.current.getBoundingClientRect();
      const popoverHeight=320;
      const popoverWidth=280;
      let top=rect.bottom + 4;
      let left=rect.left;
      if (top + popoverHeight > window.innerHeight) {
        top=rect.top - popoverHeight - 4;
      }
      if (left + popoverWidth > window.innerWidth) {
        left=window.innerWidth - popoverWidth - 16;
      }
      setPopoverStyle({
        position: 'fixed',
        top: Math.max(8,top),
        left: Math.max(8,left),
        zIndex: 9999
      });
    };
    updatePosition();
    window.addEventListener('scroll',updatePosition,true);
    window.addEventListener('resize',updatePosition);
    return ()=> {
      window.removeEventListener('scroll',updatePosition,true);
      window.removeEventListener('resize',updatePosition);
    };
  },[isOpen]);

  useEffect(()=> {
    if (!isOpen) return;
    const handleClickOutside=(e: MouseEvent)=> {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node) && 
          containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closeCalendar();
      }
    };
    document.addEventListener('mousedown',handleClickOutside);
    return ()=> document.removeEventListener('mousedown',handleClickOutside);
  },[isOpen,closeCalendar]);

  const selectDate=(y: number,m: number,d: number)=> {
    const norm=`${y}-${String(m + 1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
    if (minDate && norm < minDate) return;
    if (maxDate && norm > maxDate) return;
    setLocalError('');
    onChange(norm);
    closeCalendar();
  };

  const renderCalendar=()=> {
    const daysInMonth=new Date(currentYear,currentMonth + 1,0).getDate();
    let firstDay=new Date(currentYear,currentMonth,1).getDay();
    firstDay=firstDay===0 ? 6 : firstDay - 1; // Monday first

    const days=[];
    for (let i=0; i < firstDay; i++) days.push(null);
    for (let i=1; i <= daysInMonth; i++) days.push(i);

    const monthNames=['January','February','March','April','May','June','July','August','September','October','November','December'];
    const todayStamp=getSydneyDateStamp();

    return createPortal(
      <div 
        ref={popoverRef}
        style={popoverStyle}
        className="w-[280px] bg-white rounded-xl shadow-xl border border-slate-200 p-3 font-sans"
        role="dialog"
        aria-modal="false"
        aria-label="Calendar selection"
      >
        <div className="flex items-center justify-between mb-3">
          <button type="button" onClick={()=> {
            if (currentMonth===0) { setCurrentMonth(11); setCurrentYear(currentYear - 1); }
            else { setCurrentMonth(currentMonth - 1); }
          }} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600 focus:outline-none focus:ring-2 focus:ring-[#0F766E]" aria-label="Previous month">
            <SafeIcon icon={FiChevronLeft} className="w-4 h-4" />
          </button>
          <div className="flex gap-1">
            <select value={currentMonth} onChange={e=> setCurrentMonth(parseInt(e.target.value))} className="text-sm font-medium bg-transparent border-none outline-none cursor-pointer text-slate-800 hover:bg-slate-50 rounded p-1">
              {monthNames.map((m,i)=> <option key={m} value={i}>{m}</option>)}
            </select>
            <input type="number" value={currentYear} onChange={e=> setCurrentYear(parseInt(e.target.value) || new Date().getFullYear())} className="w-16 text-sm font-medium bg-transparent border-none outline-none cursor-pointer text-slate-800 hover:bg-slate-50 rounded p-1 text-center" />
          </div>
          <button type="button" onClick={()=> {
            if (currentMonth===11) { setCurrentMonth(0); setCurrentYear(currentYear + 1); }
            else { setCurrentMonth(currentMonth + 1); }
          }} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-600 focus:outline-none focus:ring-2 focus:ring-[#0F766E]" aria-label="Next month">
            <SafeIcon icon={FiChevronRight} className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1 mb-1 text-center">
          {['Mo','Tu','We','Th','Fr','Sa','Su'].map(d=> (
            <div key={d} className="text-[10px] font-semibold text-slate-400 py-1">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {days.map((d,i)=> {
            if (d===null) return <div key={`empty-${i}`} className="h-8" />;
            const thisStamp=`${currentYear}-${String(currentMonth + 1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
            const isToday=thisStamp===todayStamp;
            const isSelected=thisStamp===value;
            const isOutOfRange=Boolean((minDate && thisStamp < minDate) || (maxDate && thisStamp > maxDate));
            
            let btnClass="h-8 w-full rounded-md text-sm flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-[#0F766E] ";
            if (isOutOfRange) btnClass += "text-slate-300 cursor-not-allowed ";
            else if (isSelected) btnClass += "bg-[#102D4C] text-white font-medium shadow-sm ";
            else if (isToday) btnClass += "bg-[#0F766E]/10 text-[#0F766E] font-medium hover:bg-[#0F766E]/20 ";
            else btnClass += "text-slate-700 hover:bg-slate-100 ";

            return (
              <button 
                key={d} 
                type="button"
                disabled={isOutOfRange}
                onClick={()=> selectDate(currentYear,currentMonth,d)}
                className={btnClass}
                aria-label={`${d} ${monthNames[currentMonth]} ${currentYear}`}
                aria-pressed={isSelected}
              >
                {d}
              </button>
            );
          })}
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
          <button type="button" onClick={()=> {onChange('');closeCalendar();}} className="text-xs font-medium text-slate-500 hover:text-slate-700 px-2 py-1 rounded hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-[#0F766E]">Clear</button>
          <div className="flex gap-2">
            <button type="button" onClick={closeCalendar} className="text-xs font-medium text-slate-500 hover:text-slate-700 px-2 py-1 rounded hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-[#0F766E]">Close</button>
            <button type="button" onClick={()=> {
              const td=getSydneyDateStamp().split('-');
              selectDate(parseInt(td[0]),parseInt(td[1])-1,parseInt(td[2]));
            }} className="text-xs font-medium text-[#0F766E] hover:text-[#0B2138] px-2 py-1 rounded hover:bg-[#0F766E]/10 focus:outline-none focus:ring-2 focus:ring-[#0F766E]">Today</button>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  const finalAriaDescribedBy=[ariaDescribedBy,localError ? `${id}-error` : null].filter(Boolean).join(' ') || undefined;

  return (
    <div className="relative" ref={containerRef}>
      {label && <label htmlFor={id} className="block text-sm font-medium text-slate-700 mb-1">{label} {required && <span className="text-red-500">*</span>}</label>}
      <div className="relative">
        <input
          id={id}
          ref={inputRef}
          type="text"
          value={displayValue}
          onChange={e=> {setDisplayValue(e.target.value);setLocalError('');}}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder="DD/MM/YYYY"
          aria-invalid={!!(error || localError)}
          aria-describedby={finalAriaDescribedBy}
          className={`w-full pl-3 pr-10 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 transition-colors ${
            disabled ? 'bg-slate-50 text-slate-500 border-slate-200 cursor-not-allowed' : 
            (error || localError) ? 'border-red-300 focus:ring-red-500 bg-red-50/30' : 
            'border-slate-300 focus:ring-[#0F766E] bg-white'
          }`}
        />
        <button
          type="button"
          disabled={disabled}
          onClick={openCalendar}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-[#0F766E] hover:bg-slate-50 rounded focus:outline-none focus:ring-2 focus:ring-[#0F766E] disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Open calendar"
        >
          <SafeIcon icon={FiCalendar} className="w-4 h-4" />
        </button>
      </div>
      {(error || localError) && (
        <p id={`${id}-error`} className="text-xs text-red-600 mt-1">{error || localError}</p>
      )}
      {isOpen && renderCalendar()}
    </div>
  );
}